'use client';

import React from 'react';
import { ArrowLeft, Sparkles, UserCircle, Globe } from 'lucide-react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import type { WizardStep } from './FashionDiscoveryClient';
import { useAuth } from '@/contexts/AuthContext';
import NotificationBell from './NotificationBell';

interface WizardHeaderProps {
  currentStep: WizardStep;
  stepIndex: number;
  totalSteps: number;
  onBack?: () => void;
}

const STEP_LABELS: Record<string, string> = {
  occasion: 'The Occasion',
  budget: 'Your Budget',
  gender: 'Style For',
  appearance: 'Your Look',
};

export default function WizardHeader({ currentStep, stepIndex, totalSteps, onBack }: WizardHeaderProps) {
  const progressPercent = ((stepIndex) / totalSteps) * 100;
  const { user } = useAuth();

  return (
    <header className="sticky top-0 z-50 border-b" style={{ backgroundColor: '#FAF7F2', borderColor: 'hsl(35 20% 88%)' }}>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Left: Back button or logo */}
          <div className="flex items-center gap-3 sm:gap-4">
            {onBack ? (
              <button
                onClick={onBack}
                className="flex items-center gap-1.5 sm:gap-2 text-sm font-medium transition-all duration-150 hover:opacity-70 active:scale-95"
                style={{ color: 'hsl(30 8% 52%)' }}
                aria-label="Go back"
              >
                <ArrowLeft size={15} />
                <span className="hidden sm:inline">Back</span>
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <AppLogo size={24} />
                <span className="font-serif-display text-lg sm:text-xl tracking-tight" style={{ color: '#1A1A1A' }}>
                  Atelier.ai
                </span>
              </div>
            )}
          </div>

          {/* Center: Step label */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Sparkles size={13} style={{ color: '#C9A96E' }} />
            <span className="text-xs sm:text-sm font-medium tracking-wide uppercase" style={{ color: '#C9A96E', letterSpacing: '0.08em' }}>
              {STEP_LABELS[currentStep] ?? currentStep}
            </span>
          </div>

          {/* Right: Account link or step count */}
          <div className="flex items-center gap-3">
            <Link
              href="/public-feed"
              className="flex items-center gap-1.5 text-xs sm:text-sm font-medium transition-all hover:opacity-70"
              style={{ color: 'hsl(30 8% 52%)' }}
              aria-label="Public feed"
            >
              <Globe size={16} style={{ color: '#C9A96E' }} />
              <span className="hidden sm:inline">Feed</span>
            </Link>
            {user ? (
              <>
                <NotificationBell />
                <Link
                  href="/account"
                  className="flex items-center gap-1.5 text-xs sm:text-sm font-medium transition-all hover:opacity-70"
                  style={{ color: 'hsl(30 8% 52%)' }}
                  aria-label="My account"
                >
                  <UserCircle size={20} style={{ color: '#C9A96E' }} />
                  <span className="hidden sm:inline">Account</span>
                </Link>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <div className="text-xs sm:text-sm tabular-nums" style={{ color: 'hsl(30 8% 52%)' }}>
                  <span className="font-semibold" style={{ color: '#1A1A1A' }}>{stepIndex + 1}</span>
                  <span> / {totalSteps}</span>
                </div>
                <Link
                  href="/login"
                  className="flex items-center gap-1.5 text-xs sm:text-sm font-medium transition-all hover:opacity-70"
                  style={{ color: 'hsl(30 8% 52%)' }}
                  aria-label="Sign in"
                >
                  <UserCircle size={20} style={{ color: '#C9A96E' }} />
                  <span className="hidden sm:inline">Sign In</span>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-0.5 w-full rounded-full overflow-hidden" style={{ backgroundColor: 'hsl(35 15% 92%)' }}>
          <div
            className="h-full rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progressPercent}%`, backgroundColor: '#C9A96E' }}
          />
        </div>
      </div>
    </header>
  );
}